import { useState, useEffect, useMemo } from "react";
import DOMPurify from "isomorphic-dompurify";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, ShoppingCart, Truck, Shield, Award } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { FloatingWhatsApp } from "@/components/ui/floating-whatsapp";
import { ProductImageSwiper, ProductImage } from "@/components/catalog/ProductImageSwiper";
import ProductDetailSkeleton from "@/components/catalog/ProductDetailSkeleton";
import { formatPrice } from "@/utils/format";
import { generateWhatsAppLink } from "@/utils/whatsapp";
import { trackViewItem, trackAddToCart } from "@/utils/gtm";
import { useProduct } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading, error } = useProduct(slug || '');
  const [selectedVariation, setSelectedVariation] = useState(product?.variations?.[0]);
  const [activeImageId, setActiveImageId] = useState<string | undefined>();
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    if (product?.variations && product.variations.length > 0) {
      setSelectedVariation(product.variations[0]);
    }
  }, [product]);

  // Track view_item event when product loads
  useEffect(() => {
    if (product) {
      trackViewItem({
        id: product.id,
        name: product.name,
        price: product.variations?.[0]?.price || product.price,
        brand: product.brand?.name,
        category: product.categories?.[0]?.name,
        variant: product.variations?.[0]?.name,
      });
    }
  }, [product?.id]);

  // Build array of all unique images (main + variations with images)
  const allImages = useMemo<ProductImage[]>(() => {
    if (!product) return [];
    
    const images: ProductImage[] = [];
    
    // Add main product images
    if (product.images && product.images.length > 0) {
      product.images.forEach((img, index) => {
        images.push({ id: `main-${index}`, src: img, alt: product.name });
      });
    }
    
    // Add variation images (only unique ones)
    product.variations?.forEach(variation => {
      if (variation.image_url && !images.some(img => img.src === variation.image_url)) {
        images.push({ 
          id: variation.id, 
          src: variation.image_url, 
          alt: `${product.name} - ${variation.name}` 
        });
      }
    });
    
    return images;
  }, [product]);

  // Handle variation selection - navigate to its image if it has one
  const handleVariationSelect = (variation: typeof selectedVariation) => {
    setSelectedVariation(variation);
    
    // If variation has an image, navigate to it
    if (variation?.image_url) {
      const imageEntry = allImages.find(img => img.src === variation.image_url);
      if (imageEntry) {
        setActiveImageId(imageEntry.id);
      }
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    const cartItemId = selectedVariation 
      ? `${product.id}-${selectedVariation.id}` 
      : product.id;

    const itemPrice = selectedVariation?.price || product.price;

    addItem({
      id: cartItemId,
      productId: product.id,
      productSlug: product.slug,
      name: product.name,
      variationId: selectedVariation?.id,
      variationName: selectedVariation?.name,
      price: itemPrice,
      imageUrl: selectedVariation?.image_url || product.images?.[0] || '',
    });

    // Track add_to_cart event
    trackAddToCart({
      id: product.id,
      name: product.name,
      price: itemPrice,
      brand: product.brand?.name,
      category: product.categories?.[0]?.name,
      variant: selectedVariation?.name,
      quantity: 1,
    });

    toast.success("Producto agregado al carrito");
    setIsOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Skeleton className="h-5 w-36 mb-8" />
          <ProductDetailSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold mb-4">Producto no encontrado</h1>
          <Link to="/catalogo">
            <Button variant="outline">Volver al catálogo</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleWhatsAppClick = () => {
    const productUrl = window.location.href;
    const link = generateWhatsAppLink(
      product.name,
      selectedVariation?.name || "Sin variación",
      productUrl
    );
    window.open(link, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  };

  // Calculate discount
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - (selectedVariation?.price || product.sale_price!) / product.price) * 100) 
    : 0;

  // Generate SEO data with fallbacks
  const seoTitle = product.meta_title || product.name;
  const seoDescription = product.meta_description || product.short_description || 
    `Comprá ${product.name} en Tekton. ${product.brand?.name ? `Marca ${product.brand.name}.` : ''} Productos de tecnología en Costa Rica.`;
  const seoImage = product.images?.[0] || undefined;
  const seoKeywords = `${product.name}, ${product.brand?.name || ''}, tecnología, Costa Rica`.replace(/, ,/g, ',');
  const category = product.categories?.[0];

  // Build breadcrumbs for structured data
  const breadcrumbs = [
    { name: "Inicio", url: "/" },
    { name: "Catálogo", url: "/catalogo" },
    ...(category ? [{ name: category.name, url: `/catalogo?category=${category.slug}` }] : []),
    { name: product.name, url: `/producto/${product.slug}` },
  ];

  // Build array of all product images for JSON-LD
  const allProductImageUrls = allImages.map(img => img.src);

  // Build variations data for SEO (AggregateOffer and availability)
  const variationsForSeo = product.variations?.map(v => ({
    price: v.price,
    stock: v.stock_quantity || 0,
    name: v.name,
  }));

  const benefits = [
    { icon: Truck, text: "Envío a todo el país" },
    { icon: Shield, text: "Garantía incluida" },
    { icon: Award, text: "Asesoramiento personalizado" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        type="product"
        product={{
          name: product.name,
          description: product.short_description || undefined,
          image: allProductImageUrls.length > 0 ? allProductImageUrls : undefined,
          price: selectedVariation?.price || product.price,
          brand: product.brand?.name,
          category: category?.name,
          sku: selectedVariation?.sku || product.id,
          condition: "NewCondition",
          variations: variationsForSeo,
        }}
        breadcrumbs={breadcrumbs}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Back button */}
        <Link 
          to="/catalogo" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors group"
        >
          <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Volver al catálogo
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Image */}
          <ProductImageSwiper 
            images={allImages} 
            activeImageId={activeImageId}
          />

          {/* Product Info */}
          <div className="space-y-6">
            {/* Brand & badges */}
            <div className="flex items-center gap-2 flex-wrap">
              {product.brand && (
                <Badge variant="secondary">
                  {product.brand.name}
                </Badge>
              )}
              {hasDiscount && discountPercent > 0 && (
                <Badge variant="destructive">
                  -{discountPercent}% OFF
                </Badge>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold">{product.name}</h1>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <p className="text-3xl font-bold text-primary">
                {formatPrice(selectedVariation?.price || product.price)}
              </p>
              {hasDiscount && (
                <p className="text-lg text-muted-foreground line-through">
                  {formatPrice(product.price)}
                </p>
              )}
            </div>

            {product.description && (
              <div 
                className="prose prose-sm max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(product.description) 
                }} 
              />
            )}

            {/* Variations */}
            {product.variations && product.variations.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-semibold">Seleccioná una opción:</label>
                <div className="flex flex-wrap gap-2">
                  {product.variations.map((variation) => (
                    <Button
                      key={variation.id}
                      variant={selectedVariation?.id === variation.id ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleVariationSelect(variation)}
                    >
                      {variation.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  variant="cta"
                  className="flex-1"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Agregar al Carrito
                </Button>
                <Button
                  size="lg"
                  variant="accent"
                  className="flex-1"
                  onClick={handleWhatsAppClick}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Consultar por WhatsApp
                </Button>
              </div>
            </div>

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div className="pt-6 border-t">
                <h3 className="font-semibold mb-4">Especificaciones</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <dt className="text-muted-foreground">{key}:</dt>
                      <dd className="font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Benefits */}
            <div className="pt-6 border-t grid sm:grid-cols-3 gap-3">
              {benefits.map((benefit) => (
                <div 
                  key={benefit.text}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg",
                    "bg-muted border border-border"
                  )}
                >
                  <benefit.icon className="h-5 w-5 text-accent flex-shrink-0" />
                  <p className="text-sm font-medium">{benefit.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default ProductDetail;
